import type { MixpanelClient } from './mixpanel';

// PII-safe marketing attribution props shared by the waitlist funnel events.
// Only utm params, the referrer domain, and the landing path — never raw
// referrer/landing URLs or any field VALUES (email/name/etc).
type WaitlistAttributionProps = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer_domain?: string;
  landing_path?: string;
};

// Event ownership: actions that mutate server state (lead.created, lead.deleted,
// lead.stage_changed, lead.note_added, lead.consent_updated, agent.executed,
// voice.call_summary_edited) are tracked by the backend, after the mutation has
// actually committed, and also fire for the AI agent, the CSV importer and
// webhooks — paths no client hook observes. Declaring them here again let the
// client double-count every one of them. The client keeps what the server cannot
// see: UI intent, navigation, validation failures and public page views.
export type EventMap = {
  'lead.updated': { fields_changed?: string[] };
  'lead.posts_refreshed': Record<string, never>;
  'lead.enrichment_retried': { leadId?: string };
  'lead.enrichment_deeplink_triggered': { leadId?: string };
  'lead.call_human_initiated': { leadId?: string };
  'lead.call_agent_selected': { leadId?: string };

  'campaign.created': Record<string, never>;
  'campaign.updated': Record<string, never>;
  'campaign.deleted': Record<string, never>;
  'campaign.activated': Record<string, never>;
  'campaign.completed': Record<string, never>;
  'campaign.leads_added': { lead_count?: number };
  'campaign.leads_removed': { lead_count?: number };

  'segment.created': Record<string, never>;
  'segment.updated': Record<string, never>;
  'segment.deleted': Record<string, never>;
  'segment.executed': Record<string, never>;

  'agent.cancelled': Record<string, never>;
  'agent.config_updated': Record<string, never>;
  'agent.pipeline_executed': Record<string, never>;
  'agent.chat_message_sent': { agent_type?: string };

  'tool_action.approved': Record<string, never>;
  'tool_action.rejected': Record<string, never>;

  'voice.template_created': Record<string, never>;
  'voice.template_deleted': Record<string, never>;
  'voice.job_created': Record<string, never>;
  'voice.campaign_started': Record<string, never>;
  'voice.campaign_paused': Record<string, never>;
  'voice.campaign_cancelled': Record<string, never>;
  'voice.call_analysis_reprocessed': { call_id?: string };

  'voice.auto_call_triggered': { leadId?: string };

  'org.invite_created': Record<string, never>;
  'org.member_role_updated': Record<string, never>;
  'org.member_removed': Record<string, never>;
  'org.seat_limit_upsell_shown': { plan?: string; current_seats?: number; max_seats?: number };

  'org.settings_updated': { setting?: string };
  'workflow.triggered': Record<string, never>;
  'workflow.execution_completed': {
    workflow_type?: string;
    leads_created?: number;
    leads_updated?: number;
    duration_ms?: number;
  };
  'workflow.execution_failed': {
    workflow_type?: string;
    error_message?: string;
    failure_reason?: string;
  };

  'web_search.triggered': { lead_has_email?: boolean; lead_has_phone?: boolean };
  'web_search.completed': { contacts_found?: number; links_explored?: number };
  'web_search.confirmed': { contacts_saved?: number; email_saved?: boolean; phone_saved?: boolean };
  'web_search.cancelled': Record<string, never>;
  'web_search.failed': { error_message?: string };
  'web_search.no_results': { links_explored?: number };

  'prospect_search.contact_channel_opened': {
    channel: 'phone' | 'email' | 'website' | 'instagram';
  };

  'auth.user_logged_out': Record<string, never>;

  'auth.magic_link_requested': { has_invite: boolean };
  'auth.magic_link_sent': { has_invite: boolean };
  'auth.magic_link_resent': { has_invite: boolean };
  'auth.magic_link_consumed': { has_invite: boolean };
  'auth.login_succeeded': {
    connection: string;
    is_migration: boolean;
    // Separates an acquisition from a returning login. Without it a campaign's
    // signup ratio counts every repeat visit as a conversion.
    is_new_user?: boolean;
  };
  // The OTP is typed back into the app. Between magic_link_sent and
  // magic_link_consumed nothing else fires, so a visitor who mistypes the code
  // and gives up is otherwise indistinguishable from one who never opened the
  // email — two opposite product problems with the same silence.
  'auth.otp_submitted': { has_invite: boolean; is_resent_code?: boolean };
  'auth.otp_failed': {
    error_code: 'invalid_code' | 'account_disabled' | 'unknown';
  };
  'auth.login_failed': { connection?: string; error_code?: string };
  'auth.magic_link_send_failed': {
    error_code: 'rate_limited' | 'invalid_email' | 'connection_disabled' | 'unknown';
    auth0_error_code?: string;
  };

  // Voice agents onboarding wizard (Fase 2 · H12).
  'voice_agent.onboarding_step_completed': {
    step: string;
    step_index: number;
    duration_ms?: number;
  };
  'voice_agent.onboarding_completed': {
    agent_id?: string;
    total_duration_ms?: number;
  };
  'voice_agent.onboarding_skipped': {
    at_step?: string;
  };
  'voice_agent.tour_dismissed': {
    completed_all_tips?: boolean;
  };

  // Landing + waitlist marketing funnel. PII-safe: never send field VALUES,
  // only presence/shape (filled field names, counts, error types).
  'landing.viewed': { referrer?: string; is_returning?: boolean };
  'landing.section_viewed': {
    section: 'hero' | 'features' | 'workflow' | 'pricing' | 'cta';
    depth_percent?: number;
  };
  'landing.nav_clicked': { target: 'features' | 'workflow' | 'pricing' };
  'landing.login_cta_clicked': {
    source: 'header' | 'hero' | 'pricing_plan' | 'cta_section';
    plan?: 'starter' | 'growth' | 'professional' | 'business' | 'enterprise';
    billing_period?: 'monthly' | 'yearly';
    /**
     * Where the click actually went. The same CTA now leads to signup or to
     * the waitlist depending on free capacity, so a conversion rate measured
     * without this compares two different funnels as if they were one.
     */
    destination?: '/login' | '/waitlist';
  };
  'landing.checkout_started': {
    source: 'pricing_plan';
    plan: 'starter' | 'growth' | 'professional' | 'business';
    billing_period: 'monthly' | 'yearly';
  };
  'landing.pricing_billing_toggled': { billing_period: 'monthly' | 'yearly' };
  // Fires when the buyer overrides the detected billing region. A high rate
  // here means geo detection is guessing wrong and is worth investigating.
  'landing.pricing_market_selected': { market: string; previous_market: string | null };
  'landing.feature_tile_interacted': { tile: string };
  'landing.footer_link_clicked': {
    link:
      | 'features'
      | 'pricing'
      | 'integrations'
      | 'changelog'
      | 'blog'
      | 'about'
      | 'press'
      | 'privacy'
      | 'terms'
      | 'cookie'
      | 'gdpr';
    type: 'scroll' | 'nav';
  };
  'landing.language_changed': { from: string; to: string; surface: string };
  'landing.waitlist_form_started': { first_field: 'email' | 'name' | 'company' | 'phone' };
  'landing.waitlist_validation_failed': { invalid_fields: string[]; error_count: number };
  'landing.waitlist_submitted': {
    filled_optional_fields: string[];
    optional_field_count: number;
  } & WaitlistAttributionProps;
  'landing.waitlist_success': { filled_optional_fields: string[] } & WaitlistAttributionProps;
  'landing.waitlist_failed': {
    error_type: 'already_registered' | 'validation' | 'network' | 'unknown';
    status_code?: number;
  };
  'landing.waitlist_back_to_home': Record<string, never>;

  // Public changelog page events.
  'changelog.feed_viewed': { entry_count?: number };
  'changelog.entry_viewed': { slug: string; has_version?: boolean; tag_count?: number };

  // Public blog page events.
  'blog.feed_viewed': { post_count?: number; locale?: string };
  'blog.post_viewed': { slug: string; locale?: string; category_count?: number };
};

export function trackEvent<K extends keyof EventMap>(
  mp: MixpanelClient,
  event: K,
  properties?: EventMap[K],
) {
  mp.track(event, (properties ?? {}) as Record<string, unknown>);
}
